import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import {
    DataGridTable,
    GridAction,
    ConfirmationPopup,
    notifyToast,
    ToastType,
    FormPage
} from "../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";
import { saveBranchCostConfig } from "../api-handler/api-handler";
import { useFetchListData } from "../../../hooks/useFetchListData";

const BranchCostConfigListForm = () => {
    /**************************************************
    //Form handlers & hooks
    **************************************************/
    const { t } = useTranslation();
    const navigate = useNavigate();
    const spinnerCtx = useContext(DisplaySpinnerContext);
    const settingsConfig = useSelector(selectSessionConfig);

    const IcabsBranchList = useFetchListData(settingsConfig.businessCode, settingsConfig.countryCode, "getIcabsBranchList", "branch");

    /**************************************************
    //Form state variables
    **************************************************/
    const [branchCostConfigRecordList, setBranchCostConfigRecordList] = useState([]);
    const [dataGridPageSize, setDataGridPageSize] = useState(10);
    const [totalBranchCostConfig, setTotalBranchCostConfig] = useState(0);
    const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);


    /**************************************************
    //Event definitions
    **************************************************/

    //on click of add icon for creating branch cost config
    const handleCreateBranchCostConfigClick = () => {
        navigate("/branchcostconfig");
    };

    //onclick of edit branch cost config
    const handleEditBranchCostConfigClick = async (param) => {
        let _navigationData = param.recordResponse;
        delete _navigationData.CreatedDate;
        delete _navigationData.ModifiedDate;
        delete _navigationData.CountryCode;
        delete _navigationData.BusinessCode;

        navigate(
            {
                pathname: "/branchcostconfig",
                search: createSearchParams({
                    BranchId: param.recordResponse.BranchId,
                }).toString(),
            },
            { state: _navigationData }
        );
    };

    //onclick of confirm of delete popup
    const handleConfirm = async () => {
        let _filterClause = `&branchid=${selectedRow.id}`;
        await fetchAvailableGrossBranch(_filterClause, selectedRow);
        setPopupOpen(false);
        setSelectedRow(null);
    };

    //on click of cancel of delete popup
    const handleCancel = () => {
        setSelectedRow(null);
        setPopupOpen(false);
    };

    //onClick of delete in list
    const showDeleteBranchCostPopup = (event) => {
        try {
            setSelectedRow(event);
            setPopupOpen(true);
        } catch (err) {
            alert(err);
        }
    };

    //on page change
    const handlePageSizeChange = (newPageSize) => {
        setDataGridPageSize(newPageSize);
    };

    //show review modal
    const handleShowSelectedBranchCostChanges = (event) => {
        let _navigationData = { previousPath: "/branchcostconfiglist" };
        navigate(
            {
                pathname: "/reviewdetails",
                search: createSearchParams({
                    reviewFilter: `&locationkey=${event.id}`,
                }).toString(),
            },
            { state: _navigationData }
        );
    };

    /**************************************************
    //Fetch API & utility
    **************************************************/


    const setBranchCostConfigList = (branchCostConfigList) => {
        try {
            branchCostConfigList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
            let _branchCostConfigList = branchCostConfigList.map((item) => {
                return {
                    id: item.BranchId,
                    branchId: item.BranchId,
                    brnachNumber: item.BranchNumber,
                    branchName: item.BranchName,
                    minuteCostNight: item.MinuteCostNight,
                    minuteCostDay: item.MinuteCostDay,
                    minuteCostHolidayNight: item.MinuteCostHolidayNight,
                    minuteCostHoliday: item.MinuteCostHoliday,
                    visitCost: item.VisitCost,
                    visitCostNight: item.VistCostNight,
                    visitCostHoliday: item.VisitCostHoliday,
                    visitCostHolidayNight: item.VistCostHolidayNight,
                    recordResponse: item,
                };
            });

            setBranchCostConfigRecordList(_branchCostConfigList);
            setTotalBranchCostConfig(_branchCostConfigList.length);
            spinnerCtx.setLoadingIndicator(false);
        } catch (err) {
            spinnerCtx.setLoadingIndicator(false);
            alert(err);
        }
    };

    const FetchBranchCostConfigList = async () => {
        try {
            let _lists = await PricingPortalAPI.getList(
                settingsConfig.businessCode,
                settingsConfig.countryCode,
                "getBranchCostConfigList",
                "branchcostconfig"
            );
            setBranchCostConfigList(_lists);
        } catch (err) {
            alert(err);
        }
    };
    //Fetch API for checking available gross branch with the selected market sector
    const fetchAvailableGrossBranch = async (filter, param) => {
        try {
            let _availableGrossBranch = await PricingPortalAPI.getFilterList(
                settingsConfig.businessCode,
                settingsConfig.countryCode,
                "getGrossBranchList",
                "grossbranch",
                filter
            );
            if (_availableGrossBranch && _availableGrossBranch.length > 0) {
                notifyToast("Gross branch combination exists with this market sector", ToastType.ERROR);
            } else {
                await deleteBranchCostConfig(param);
            }
        } catch (e) {
            alert(e);
        }
    };

    const formPreviousDataForDelete = (param) => {
        try {
            let _previousState = param.recordResponse;
            _previousState.Deleted = true;
            delete _previousState.CreatedDate;
            delete _previousState.ModifiedDate;
            delete _previousState.CountryCode;
            delete _previousState.BusinessCode;
            delete _previousState.Version;
            delete _previousState.BranchId;
            delete _previousState.Status;

            IcabsBranchList.forEach((item) => {
                if (item.BranchNumber === _previousState.BranchNumber) {
                    _previousState.Branch = {
                        id: item.BranchNumber,
                        label: `${item.BranchNumber} - ${item.BranchName}`,
                    };
                }
            });

            return _previousState ? _previousState : null;
        } catch (e) {
            alert(e);
        }
    };

    //Delete API call
    const deleteBranchCostConfig = async (param) => {
        try {
            spinnerCtx.setLoadingIndicator(true);
            let EditBranchId = param.id;
            const status = await saveBranchCostConfig(formPreviousDataForDelete(param), {
                EditBranchId,
                settingsConfig
            });

            if (status?.responseStatus === 200) {
                notifyToast("Sent for Approval", ToastType.SUCCESS);
                await FetchBranchCostConfigList();
            } else {
                notifyToast(status.errorMessage ? status.errorMessage : "Branch Cost Config deletion failed", ToastType.ERROR);
                spinnerCtx.setLoadingIndicator(false);
            }
        } catch (err) {
            alert(err);
        }
    };

    /**************************************************
    //JSX delcaration
    **************************************************/

    //data grid schema
    const dataGridSchema = [
        {
            field: "brnachNumber",
            headerName: t("branchCostConfigList.brnachNumber"),
            flex: 1,
        },
        {
            field: "branchName",
            headerName: t("branchCostConfigList.branchName"),
            flex: 1,
        },
        {
            field: "minuteCostNight",
            headerName: t("branchCostConfigList.minuteCostNight"),
            flex: 1,
        },
        {
            field: "minuteCostDay",
            headerName: t("branchCostConfigList.minuteCostDay"),
            flex: 1,
        },
        {
            field: "minuteCostHolidayNight",
            headerName: t("branchCostConfigList.minuteCostHolidayNight"),
            flex: 1,
        },
        {
            field: "minuteCostHoliday",
            headerName: t("branchCostConfigList.minuteCostHoliday"),
            flex: 1,
        },
        {
            field: "visitCost",
            headerName: t("branchCostConfigList.visitCost"),
            flex: 1,
        },
        {
            field: "visitCostNight",
            headerName: t("branchCostConfigList.visitCostNight"),
            flex: 1,
        },
        {
            field: "visitCostHoliday",
            headerName: t("branchCostConfigList.visitCostHoliday"),
            flex: 1,
        },
        {
            field: "visitCostHolidayNight",
            headerName: t("branchCostConfigList.visitCostHolidayNight"),
            flex: 1,
        },
        {
            field: "recordResponse",
            hide: true,
        },
        {
            field: "action",
            headerName: t,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (
                    <GridAction actionsConfig={actionConfigs} row={params.row} />
                );
            },
        },
    ];

    //button configs for add and download list
    const buttonConfigs = [
        {
            buttonText: <AddIcon />,
            buttonType: "filled",
            onClick: handleCreateBranchCostConfigClick,
            buttonColor: "#A62B23",
            textColor: "#fff",
            action: "create"
        },
        {
            buttonText: <SystemUpdateAltIcon />,
            buttonType: "outlined",
            onClick: () => {
                AppUtility.handleDownload(branchCostConfigRecordList, "branchCostConfig.xlsx");
            },
            buttonColor: "#A62B23",
            textColor: "#A62B23",
            action: "download"
        },
    ];

    //action configs for edit and delete record
    const actionConfigs = [
        {
            onClick: handleShowSelectedBranchCostChanges,
            action: "review"
        },
        {
            icon: MdCreate,
            onClick: handleEditBranchCostConfigClick,
            buttonColor: "#EEEEE",
            action: "edit"
        },
        {
            icon: MdDelete,
            onClick: showDeleteBranchCostPopup,
            buttonColor: "#A62B23",
            action: "delete"
        },
    ];


    /**************************************************
    //Form on load useEffect
    **************************************************/
    useEffect(() => {

        spinnerCtx.setLoadingIndicator(true);
        FetchBranchCostConfigList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <FormPage isList={true}>
            <DataGridTable
                rows={branchCostConfigRecordList}
                columns={dataGridSchema}
                pageSize={dataGridPageSize}
                pageSizeOptions={gridPageSizeOptions}
                onPageSizeChange={handlePageSizeChange}
                totalRowCount={totalBranchCostConfig}
                toolbarHeader={t("branchCostConfigList.header")}
                toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
                buttonConfigs={buttonConfigs}
            />
            <ConfirmationPopup
                isOpen={popupOpen}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                confirmationText={t("popup.deleteBranchCostConfigConfirmationMessage")}
                eventObj={selectedRow}
            />
        </FormPage>
    );
};

export default BranchCostConfigListForm;
